import { cn } from '@/lib/utils';
import type { HTMLAttributes, ReactNode } from 'react';

interface GlassCardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Adds a subtle gradient glow that brightens on hover. Default true. */
  glow?: boolean;
  /** Extra padding preset. */
  padding?: 'sm' | 'md' | 'lg';
}

const paddingMap = {
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

/**
 * The one reusable glass panel for the whole app — translucent frosted glass
 * with a 1px soft gradient border (green -> purple) and a subtle inner glow
 * on hover. Elevation comes from glow + blur, never a hard drop shadow.
 */
export function GlassCard({
  children,
  glow = true,
  padding = 'md',
  className,
  ...props
}: GlassCardProps) {
  return (
    <div
      className={cn(
        'group relative rounded-2xl bg-gradient-to-br from-primary/40 via-border/80 to-accent/40 p-[1px] transition-all duration-500',
        glow && 'hover:from-primary/60 hover:to-accent/60',
        className
      )}
      {...props}
    >
      <div
        className={cn(
          'glass-surface relative h-full w-full rounded-2xl',
          paddingMap[padding]
        )}
      >
        {glow && (
          <div className='pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 bg-gradient-to-br from-primary/[0.06] via-transparent to-accent/[0.08]' />
        )}
        <div className='relative'>{children}</div>
      </div>
    </div>
  );
}

export default GlassCard;
